import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:2000/api'
  : 'https://bomb-game-two.vercel.app/api';

const TRANSLATIONS = {
  en: {
    title: 'Bomb Game',
    startGame: 'Start Game',
    newGame: 'New Game',
    settings: 'Settings',
    language: 'Language',
    tickingSound: 'Ticking Sound',
    explosionSound: 'Explosion Sound',
    error: {
      gameStart: 'Failed to start game',
      soundEffects: 'Sound effects unavailable'
    }
  },
  ru: {
    title: 'Игра Бомба',
    startGame: 'Начать игру',
    newGame: 'Новая игра',
    settings: 'Настройки',
    language: 'Язык',
    tickingSound: 'Звук тиканья',
    explosionSound: 'Звук взрыва',
    error: {
      gameStart: 'Не удалось начать игру',
      soundEffects: 'Звуковые эффекты недоступны'
    }
  }
};

const SOUND_CONFIG = {
  ticking: {
    src: '/assets/ticking.mp3',
    volume: 0.5,
    loop: true
  },
  explosion: {
    src: '/assets/explosion.mp3',
    volume: 0.7,
    loop: false
  }
};

// Screen states
const SCREENS = {
  MAIN_MENU: 'MAIN_MENU',
  GAME: 'GAME',
  SETTINGS: 'SETTINGS'
};

// Axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000
});

function App() {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.MAIN_MENU);
  const [gameState, setGameState] = useState({
    isActive: false,
    message: ''
  });
  const [timeLeft, setTimeLeft] = useState(0);
  const [error, setError] = useState('');
  const [initialTime, setInitialTime] = useState(0);
  const [hasGameEverStarted, setHasGameEverStarted] = useState(false);
  const [soundsLoaded, setSoundsLoaded] = useState(false);
  const [lastTask, setLastTask] = useState('');
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'en';
  });
  const [soundSettings, setSoundSettings] = useState(() => {
    const savedSettings = localStorage.getItem('soundSettings');
    return savedSettings ? JSON.parse(savedSettings) : {
      tickingSound: true,
      explosionSound: true
    };
  });
  
  // Save sound settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('soundSettings', JSON.stringify(soundSettings));
  }, [soundSettings]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Get translations for current language
  const t = TRANSLATIONS[language];

  const sounds = useRef({
    ticking: null,
    explosion: null
  });
  const timerRef = useRef(null);

  // Initialize sounds
  const initializeSounds = useCallback(async () => {
    if (soundsLoaded) return;

    try {
      const soundPromises = Object.entries(SOUND_CONFIG).map(([key, config]) => {
        const audio = new Audio(`${process.env.PUBLIC_URL}${config.src}`);
        audio.volume = config.volume;
        audio.loop = config.loop;
        sounds.current[key] = audio;
        return audio.load();
      });

      await Promise.all(soundPromises);
      setSoundsLoaded(true);
    } catch (error) {
      console.error('Sound initialization error:', error);
      setError(t.error.soundEffects);
    }
  }, [soundsLoaded, t.error.soundEffects]);

  // Sound management
  const handleSound = useCallback((type, action) => {
    const sound = sounds.current[type];
    if (!sound) return;

    // Check if sound type is enabled in settings
    const isEnabled = type === 'ticking' ? soundSettings.tickingSound : soundSettings.explosionSound;
    if (!isEnabled) return;

    try {
      if (action === 'play') {
        if (sound.paused) {
          sound.currentTime = 0;
          sound.play().catch(e => console.error(`${type} playback failed:`, e));
        }
      } else {
        sound.pause();
        sound.currentTime = 0;
      }
    } catch (error) {
      console.error(`Sound ${action} error:`, error);
    }
  }, [soundSettings]);

  // Cleanup sounds
  useEffect(() => {
    return () => {
      Object.values(sounds.current).forEach(sound => {
        if (sound) {
          sound.pause();
          sound.src = '';
        }
      });
      sounds.current = {};
    };
  }, []);

  // Client-side timer logic
  useEffect(() => {
    if (gameState.isActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            // Stop timer and game when it reaches 0
            clearInterval(timerRef.current);
            setGameState(prev => ({ ...prev, isActive: false }));
            handleSound('ticking', 'stop');
            handleSound('explosion', 'play');
            setLastTask(gameState.message);
            return 0;
          }
          return newTime;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [gameState.isActive, handleSound, gameState.message]);

  // Start new game
  const startGame = async () => {
    try {
      await initializeSounds();
      
      // Stop all sounds and clear existing timer
      Object.keys(sounds.current).forEach(key => handleSound(key, 'stop'));
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      const { data } = await api.post('/game/start', { language });
      setGameState({
        isActive: true,
        message: data.task
      });
      setTimeLeft(data.timeLeft);
      setInitialTime(data.timeLeft);
      setHasGameEverStarted(true);
      setError('');
      handleSound('ticking', 'play');
      setCurrentScreen(SCREENS.GAME);
    } catch (error) {
      console.error('Game start error:', error);
      setError(t.error.gameStart);
    }
  };

  const getFuseProgress = useCallback(() => {
    if (!gameState.isActive && !hasGameEverStarted) return 100;
    if (!gameState.isActive && timeLeft === 0) return 0;
    if (!initialTime) return 100;
    return Math.max((timeLeft / initialTime) * 100, 0);
  }, [gameState.isActive, timeLeft, initialTime, hasGameEverStarted]);

  const handleBackToMenu = () => {
    setCurrentScreen(SCREENS.MAIN_MENU);
    setGameState({ isActive: false, message: '' });
    setTimeLeft(0);
    setHasGameEverStarted(false);
    Object.keys(sounds.current).forEach(key => handleSound(key, 'stop'));
  };

  const renderMainMenu = () => (
    <div className="main-menu">
      <h1>{t.title}</h1>
      <div className="menu-buttons">
        <button onClick={startGame} className="menu-button start-button">
          {t.startGame}
        </button>
        <button onClick={() => setCurrentScreen(SCREENS.SETTINGS)} className="menu-button settings-button">
          {t.settings}
        </button>
      </div>
    </div>
  );

  const renderGame = () => (
    <div className="game-screen">
      <button onClick={handleBackToMenu} className="back-button" />
      
      <div className="game-content">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {gameState.isActive ? (
          <>
            {gameState.message && (
              <div className="word-prompt">
                {gameState.message}
              </div>
            )}
            <div className="bomb-container">
              <div className="fuse-container">
                <div 
                  className="fuse" 
                  style={{ 
                    width: `${getFuseProgress()}%`,
                    opacity: 1,
                    transition: 'width 0.1s linear'
                  }}
                >
                  {gameState.isActive && <div className="spark"></div>}
                </div>
              </div>
              <div 
                className={`bomb ${(!gameState.isActive && timeLeft === 0) ? 'exploded' : ''}`}
                key={timeLeft === 0 ? 'exploded' : 'normal'}
              >
                <div className="bomb-body">
                  <div className="bomb-cap"></div>
                </div>
              </div>
            </div>
          </>
        ) : (
          timeLeft === 0 && (
            <>
              {lastTask && (
                <div className="last-task">
                  {lastTask}
                </div>
              )}
              <button onClick={startGame} className="start-button">
                {t.newGame}
              </button>
            </>
          )
        )}
      </div>
    </div>
  );

  const renderSettings = () => (
    <SettingsScreen 
      onBack={() => setCurrentScreen(SCREENS.MAIN_MENU)}
      soundSettings={soundSettings}
      onSoundSettingsChange={setSoundSettings}
      language={language}
      onLanguageChange={setLanguage}
      translations={t}
    />
  );

  const renderScreen = () => {
    switch (currentScreen) {
      case SCREENS.MAIN_MENU:
        return renderMainMenu();
      case SCREENS.GAME:
        return renderGame();
      case SCREENS.SETTINGS:
        return renderSettings();
      default:
        return renderMainMenu();
    }
  };

  return (
    <div className="App">
      {renderScreen()}
    </div>
  );
}

function SettingsScreen({ 
  onBack, 
  soundSettings, 
  onSoundSettingsChange, 
  language,
  onLanguageChange,
  translations: t
}) {
  const handleTickingSoundChange = (checked) => {
    onSoundSettingsChange(prev => ({ ...prev, tickingSound: checked }));
  };

  const handleExplosionSoundChange = (checked) => {
    onSoundSettingsChange(prev => ({ ...prev, explosionSound: checked }));
  };

  return (
    <div className="settings-screen">
      <button onClick={onBack} className="back-button" />
      <h1>{t.settings}</h1>
      <div className="settings-container">
        <div className="settings-item">
          <label>{t.language}</label>
          <div className="dropdown-wrapper">
            <select 
              className="settings-dropdown"
              value={language}
              onChange={(e) => onLanguageChange(e.target.value)}
            >
              <option value="en">English</option>
              <option value="ru">Русский</option>
            </select>
          </div>
        </div>

        <div className="settings-item">
          <label>{t.tickingSound}</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={soundSettings.tickingSound}
              onChange={(e) => handleTickingSoundChange(e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>

        <div className="settings-item">
          <label>{t.explosionSound}</label>
          <label className="switch">
            <input
              type="checkbox"
              checked={soundSettings.explosionSound}
              onChange={(e) => handleExplosionSoundChange(e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>
      </div>
    </div>
  );
}

export default App;